import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { api, dateCourte } from "./api";
import { Carte, Section, Tableau, Cellule, Eyebrow, Modale } from "./ui";

/* Les morceaux de l'application des familles vue de l'école, utilisés par le menu
   « Application » (MenuApplication.jsx, de retour depuis le 20.10.2026) et le Tableau de bord.
   - CartesApplication : qui l'utilise, qui l'a installée → Tableau de bord, sous
     « Essais, auditions, stages et spectacles » (chiffres livrés par /stats).
   - AnnoncesApplication : les messages de l'écran « Aujourd'hui » → Application →
     « Annonces & notifications ».
   - ComptesTest : les comptes de test (version de test) → Application → « Voir l'app ». */

const VIDE = { titre: "", texte: "", lien: "", lien_label: "", du: "", au: "", actif: true };

const Erreur = ({ children }) => <p className="text-sm" style={{ color: "#B91C1C" }}>{children}</p>;

/* Les cinq chiffres de l'app. `resume` : le bloc « application » de /stats (ou le
   « resume » de GET /application, mêmes clés). */
export function CartesApplication({ resume: r }) {
  /* « Je vois 1 absence mais pas qui c'est » (école, 12.10.2026) : la carte ouvre la liste. */
  const [absences, setAbsences] = useState(false);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="cartes-application">
      <Carte titre="Comptes créés" valeur={r.comptes} detail={`${r.familles} familles inscrites cette saison`} testid="app-comptes" />
      <Carte titre="Adresses confirmées" valeur={r.verifies} ton="primaire" testid="app-verifies" />
      <Carte titre="Actifs (30 jours)" valeur={r.actifs_30j} ton="vert" testid="app-actifs" />
      <Carte titre="App installée" valeur={r.installees} detail="sur l'écran d'accueil" ton="primaire" testid="app-installees" />
      <button type="button" className="text-left rounded-2xl transition-transform hover:-translate-y-0.5"
        onClick={() => setAbsences(true)} data-testid="app-absences-ouvrir">
        <Carte titre="Absences annoncées" valeur={r.absences_a_venir}
          detail={r.absences_a_venir ? "à venir · cliquer pour voir qui →" : "à venir"} ton="ambre" testid="app-absences" />
      </button>
      {absences && <AbsencesAnnoncees onFermer={() => setAbsences(false)} />}
    </div>
  );
}

const jourLisible = (iso) => {
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString("fr-CH", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
};

/* Les absences à venir annoncées par les familles dans l'app : qui, quel cours, quand,
   pourquoi. Sur la feuille de présence, l'élève porte la pastille ABSENT ce jour-là. */
function AbsencesAnnoncees({ onFermer }) {
  const [liste, setListe] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => {
    api("/absences").then((d) => setListe((d.absences || []).filter((a) => !a.annulee_le)))
      .catch((e) => setErreur(e.message));
  }, []);
  return (
    <Modale titre="Absences annoncées dans l'app" large onFermer={onFermer} testid="modale-absences"
      sous_titre="Annoncées par les familles, à venir. Sur la feuille de présence, l'élève porte la pastille ABSENT ce jour-là.">
      {erreur && <Erreur>{erreur}</Erreur>}
      {!liste && !erreur && <p className="text-sm text-[var(--muted)]">Chargement…</p>}
      {liste && liste.length === 0 && <p className="text-sm text-[var(--muted)]">Aucune absence à venir.</p>}
      {liste && liste.length > 0 && (
        <ul className="divide-y" style={{ borderColor: "var(--border-soft)" }} data-testid="liste-absences">
          {liste.map((a) => (
            <li key={a.id} className="py-3 text-sm">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                <span className="font-semibold">{a.eleve || "Élève"}</span>
                <span className="text-[var(--muted)]">{a.cours_nom || "tous ses cours"}</span>
                <span className="ml-auto font-medium whitespace-nowrap">
                  {a.du === a.au ? jourLisible(a.du) : `du ${jourLisible(a.du)} au ${jourLisible(a.au)}`}
                </span>
              </div>
              {a.motif && <div className="text-xs mt-1">« {a.motif} »</div>}
              <div className="text-[11px] text-[var(--muted)] mt-0.5">
                annoncée le {dateCourte(a.annonce_le)}{a.annonce_par ? ` par ${a.annonce_par}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modale>
  );
}

/* Les annonces de l'app des familles : créer, modifier, afficher/masquer, supprimer.
   Menu Application → « Annonces & notifications » (`integre` : sans son propre titre). */
export function AnnoncesApplication({ integre = false }) {
  const [annonces, setAnnonces] = useState(null);
  const [form, setForm] = useState(null);
  const [erreur, setErreur] = useState("");
  // Un seul envoi à la fois : un double clic créait deux fois la même annonce.
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(() => {
    api("/application/annonces").then((d) => setAnnonces(d.annonces || [])).catch((e) => setErreur(e.message));
  }, []);
  useEffect(charger, [charger]);

  const enregistrer = async () => {
    if (enCours) return;
    setErreur("");
    setEnCours(true);
    try {
      if (form.id) {
        await api(`/application/annonces/${form.id}`, { method: "PUT", body: JSON.stringify(form) });
      } else {
        await api("/application/annonces", { method: "POST", body: JSON.stringify(form) });
      }
      setForm(null);
      charger();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnCours(false);
    }
  };

  const basculer = async (a) => {
    if (enCours) return;
    setErreur("");
    setEnCours(true);
    try {
      // Modification partielle : seul l'état affiché/masqué change.
      await api(`/application/annonces/${a.id}`, { method: "PUT", body: JSON.stringify({ actif: !a.actif }) });
      charger();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnCours(false);
    }
  };

  const supprimer = async (a) => {
    if (!window.confirm(`Supprimer l'annonce « ${a.titre} » ?`)) return;
    await api(`/application/annonces/${a.id}`, { method: "DELETE" });
    charger();
  };

  const champ = (cle, label, props = {}) => (
    <label className="block text-sm">
      <span className="text-xs text-[var(--muted)]">{label}</span>
      <input className="input-field mt-1 w-full" value={form[cle] || ""}
        onChange={(e) => setForm({ ...form, [cle]: e.target.value })} {...props}
        data-testid={`annonce-${cle}`} />
    </label>
  );

  return (
    <div className="space-y-8" data-testid="console-annonces">
      {!integre && (
        <div>
          <Eyebrow>Application des familles</Eyebrow>
          <h1 className="font-display text-3xl tracking-tight mt-1">Annonces de l'application</h1>
        </div>
      )}

      {!annonces ? <p className="text-[var(--muted)]">{erreur || "Chargement…"}</p> : (
        <Section titre="Annonces sur l'écran « Aujourd'hui »"
          aide="Un message de l'école pour toutes les familles : cours annulé, rappel, événement. Il s'affiche pendant la période choisie."
          action={!form && (
            <button className="btn-primary text-sm" onClick={() => setForm({ ...VIDE })} data-testid="annonce-nouvelle">
              <Plus size={14} /> Nouvelle annonce
            </button>
          )} testid="app-annonces">
          {form && (
            <div className="bg-white rounded-2xl border p-5 space-y-3" style={{ borderColor: "var(--border)" }}>
              {champ("titre", "Titre")}
              <label className="block text-sm">
                <span className="text-xs text-[var(--muted)]">Texte</span>
                <textarea className="input-field mt-1 w-full" rows={3} value={form.texte}
                  onChange={(e) => setForm({ ...form, texte: e.target.value })} data-testid="annonce-texte" />
              </label>
              <div className="grid md:grid-cols-2 gap-3">
                {champ("lien", "Lien (facultatif) — ex. /stages ou https://…")}
                {champ("lien_label", "Texte du bouton (facultatif)")}
                {champ("du", "Afficher à partir du", { type: "date" })}
                {champ("au", "Jusqu'au (vide = sans fin)", { type: "date" })}
              </div>
              {erreur && <Erreur>{erreur}</Erreur>}
              <div className="flex gap-2">
                <button className="btn-primary text-sm" onClick={enregistrer} disabled={enCours} data-testid="annonce-enregistrer">{enCours ? "Enregistrement…" : "Enregistrer"}</button>
                <button className="btn-ghost text-sm" onClick={() => { setForm(null); setErreur(""); }}>Annuler</button>
              </div>
            </div>
          )}
          {erreur && !form && <Erreur>{erreur}</Erreur>}
          <Tableau
            testid="app-annonces-liste"
            entetes={["Annonce", "Période", "Visible", ""]}
            lignes={annonces}
            vide="Aucune annonce pour l'instant."
            rendu={(a) => (
              <>
                <Cellule fort>
                  <button className="text-left underline-offset-2 hover:underline" onClick={() => { setErreur(""); setForm({ ...VIDE, ...a }); }}>{a.titre}</button>
                  <div className="text-xs font-normal text-[var(--muted)]">{a.texte}</div>
                </Cellule>
                <Cellule muet>{dateCourte(a.du)}{a.au ? ` → ${dateCourte(a.au)}` : " → sans fin"}</Cellule>
                <Cellule>
                  <button className="btn-ghost text-xs" onClick={() => basculer(a)} disabled={enCours} data-testid="annonce-basculer">
                    {a.actif ? <><Eye size={13} /> Oui</> : <><EyeOff size={13} /> Non</>}
                  </button>
                </Cellule>
                <Cellule droite>
                  <button className="btn-ghost text-xs" onClick={() => supprimer(a)} aria-label="Supprimer"><Trash2 size={13} /></button>
                </Cellule>
              </>
            )}
          />
        </Section>
      )}
    </div>
  );
}

/* Ce qui reste de fictif, en mots (réponse de /test/etat, collection → nombre). */
const RESTES = {
  console_inscriptions_auditions: "inscriptions aux auditions",
  console_inscriptions_stages: "inscriptions aux stages",
  console_inscriptions_workshops: "inscriptions aux workshops",
  console_inscriptions_spectacles: "réservations de spectacle",
  console_familles: "familles", console_eleves: "élèves", console_inscriptions: "inscriptions",
  console_facturation: "lignes de facturation", console_factures: "factures",
  console_factures_versions: "anciennes versions de facture", console_encaissements: "paiements",
  console_compteurs: "numéros réservés", console_attestations: "attestations",
  console_absences: "absences", console_essais: "essais", console_emails: "e-mails au journal",
  console_journal: "lignes d'historique", console_modifications: "corrections consignées",
  console_notifications_traitees: "lignes « traité » d'À traiter",
  espace_comptes: "comptes de l'app", admin_users: "compte direction de test",
  console_profs: "prof de test", console_appels: "coches d'appel",
};
const restesEnClair = (restes) => Object.entries(restes || {})
  .map(([nom, n]) => `${n} ${RESTES[nom] || nom.replace(/^console_/, "").replace(/_/g, " ")}`)
  .join(", ");

/* Les comptes de test (version de test seulement) : menu Application → « Voir l'app »,
   sous le simulateur. Pour essayer l'app avec de vraies connexions (s'inscrire, annoncer
   une absence…), ce que le simulateur, en lecture seule, ne fait pas. */
export function ComptesTest() {
  const [test, setTest] = useState(null);
  const [fictives, setFictives] = useState(null);
  const [erreur, setErreur] = useState("");
  const charger = useCallback(() => {
    api("/test/etat").then(setTest).catch(() => {});
  }, []);
  useEffect(charger, [charger]);

  const creerFictives = async (nouveau = false) => {
    if (!window.confirm(nouveau
      ? "Recréer les comptes de test avec un NOUVEAU mot de passe ? L'ancien ne marchera plus."
      : "Créer (ou remettre à neuf) les comptes de test ? Le mot de passe déjà donné reste le même.")) return;
    try {
      setFictives(await api(`/test/familles${nouveau ? "?nouveau_mot_de_passe=true" : ""}`, { method: "POST" }));
      charger();
    } catch (e) { setErreur(e.message); }
  };
  const effacerFictives = async () => {
    if (!window.confirm("Effacer les familles fictives et tout ce qu'elles ont créé ?")) return;
    await api("/test/familles", { method: "DELETE" });
    setFictives(null);
    charger();
  };

  if (!test?.version_de_test) return null;
  return (
    <>
      {erreur && <Erreur>{erreur}</Erreur>}
      {(
        <Section titre="Comptes de test : famille, direction, prof (version de test uniquement)"
          aide="Pour tester l'application avec trois connexions : une famille (s'inscrire, annoncer une absence…), la direction (mode école) et un prof (ses listes, ses heures). Aucun e-mail ne part sur cette version, et tout s'efface d'un clic."
          testid="app-fictives">
          <div className="bg-white rounded-2xl border p-5 space-y-3 text-sm" style={{ borderColor: "var(--border)" }}>
            <p>{test.familles ? `${test.familles} familles fictives en place.` : "Aucune famille fictive pour l'instant."}</p>
            {/* Après l'import d'une vraie sauvegarde, les familles fictives ont disparu mais
                leur audition, leurs workshops, le prof de test… pouvaient rester : on le dit,
                et « Tout effacer » reste proposé tant qu'il reste quelque chose (09.10.2026). */}
            {!test.familles && test.nb_restes > 0 && (
              <p className="rounded-xl px-4 py-3" style={{ background: "#FFFBEB", color: "#92400E" }}
                data-testid="app-fictives-restes">
                Il reste {test.nb_restes} donnée(s) fictive(s) d'un essai précédent
                ({restesEnClair(test.restes)}). « Tout effacer » les retire sans toucher aux vraies données.
              </p>
            )}
            {fictives && (
              <div className="rounded-xl p-4" style={{ background: "#ECFDF5" }} data-testid="app-fictives-acces">
                <p className="font-semibold">Connexion dans l'app (/espace, onglet « Mot de passe ») :</p>
                {fictives.comptes.map((c) => (
                  <p key={c.email}>
                    <span className="inline-block min-w-[11rem] font-medium">{c.role || "Famille"}</span>{" "}
                    <strong className="break-all">{c.email}</strong>
                    {c.enfants?.length ? ` — ${c.nom} (${c.enfants.join(", ")})` : ""}
                  </p>
                ))}
                {fictives.mot_de_passe
                  ? <p className="mt-2">Mot de passe : <strong>{fictives.mot_de_passe}</strong> — note-le, il ne sera plus affiché.</p>
                  : <p className="mt-2">Mot de passe : <strong>inchangé</strong> — celui déjà donné à l'école marche toujours.</p>}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button className="btn-primary text-sm" onClick={() => creerFictives(false)} data-testid="app-fictives-creer">
                {test.familles ? "Remettre à neuf (même mot de passe)" : "Créer les familles fictives"}
              </button>
              {test.familles > 0 && (
                <button className="btn-ghost text-sm" onClick={() => creerFictives(true)}>Nouveau mot de passe</button>
              )}
              {(test.familles > 0 || test.nb_restes > 0) && (
                <button className="btn-ghost text-sm" onClick={effacerFictives}
                  data-testid="app-fictives-effacer">Tout effacer</button>
              )}
            </div>
          </div>
        </Section>
      )}

    </>
  );
}
